import React, { useContext, useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal, Alert } from 'react-bootstrap';
import { Context } from '../index';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { $authHost } from '../http/index';
import '../styles/Basket.css';
import { createOrder } from '../http/orderAPI';
import useModalCleanup from '../hooks/useModalCleanup';
import { fetchProductsByIds } from '../http/productAPI';
import { getImageUrl } from '../utils/imageUtils';
import { toJS } from 'mobx';

const Basket = observer(() => {
    // 1. Сначала все состояния
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [orderPlaced, setOrderPlaced] = useState(false);
    const [userDiscount, setUserDiscount] = useState(0);
    const [orderError, setOrderError] = useState('');

    // 2. Затем все мемоизированные функции
    const handleModalClose = useCallback(() => {
        setShowOrderModal(false);
        setOrderPlaced(false);
    }, []);

    // 3. Затем все эффекты
    useModalCleanup(showOrderModal);

    // 4. Остальной код компонента
    const { user, product } = useContext(Context);
    const navigate = useNavigate();

    useEffect(() => {
        if (!user.isAuth) {
            navigate('/login');
        } else {
            $authHost.get('/api/user/discount-info')
                .then(res => {
                    setUserDiscount(res.data.currentDiscount || 0);
                })
                .catch(() => setUserDiscount(0));
        }
    }, [user.isAuth, navigate]);

    if (!user.isAuth) {
        return null;
    }

    const totalProductDiscount = user.basket.reduce((sum, item) => {
        const discount = Number(item.discount) || 0;
        const priceWithProductDiscount = discount > 0 ? item.price * (1 - discount / 100) : item.price;
        const totalProductDiscount = priceWithProductDiscount * item.quantity;
        return sum + totalProductDiscount;
    }, 0);
    const totalWithAllDiscounts = userDiscount > 0 ? totalProductDiscount * (1 - userDiscount / 100) : totalProductDiscount;
    const personalDiscountAmount = totalProductDiscount - totalWithAllDiscounts;

    const handleOrder = async () => {
        setOrderError('');
        try {
            if (user.basket.length === 0) {
                setOrderError('Корзина пуста');
                return;
            }
            // Группируем товары по store_id
            const itemsByStore = {};
            user.basket.forEach(item => {
                const storeId = item.store_id || 'no_store';
                if (!itemsByStore[storeId]) itemsByStore[storeId] = [];
                itemsByStore[storeId].push(item);
            });
            const storeIds = Object.keys(itemsByStore);
            // Пропорционально распределяем скидку между заказами
            const totalProductDiscount = user.basket.reduce((sum, item) => {
                const discount = Number(item.discount) || 0;
                const priceWithProductDiscount = discount > 0 ? item.price * (1 - discount / 100) : item.price;
                return sum + priceWithProductDiscount * item.quantity;
            }, 0);
            let allSuccess = true;
            let errorMsg = '';
            for (const storeId of storeIds) {
                const items = itemsByStore[storeId];
                const storeTotal = items.reduce((sum, item) => {
                    const discount = Number(item.discount) || 0;
                    const priceWithProductDiscount = discount > 0 ? item.price * (1 - discount / 100) : item.price;
                    return sum + priceWithProductDiscount * item.quantity;
                }, 0);
                // Пропорциональная персональная скидка
                const storePersonalDiscount = userDiscount > 0 ? storeTotal * (userDiscount / 100) : 0;
                // Детальное логирование скидки
                console.log('🔍 [BASKET] Скидка перед оформлением заказа:', {
                    userDiscount,
                    storePersonalDiscount,
                    totalProductDiscount,
                    totalWithAllDiscounts,
                    totalProductSum: storeTotal,
                    items: items.map(item => ({
                        id: item.id,
                        name: item.name,
                        price: item.price,
                        discount: item.discount,
                        final_price: item.final_price
                    }))
                });

                const orderData = {
                    items: items.map(item => {
                        const discount = Number(item.discount) || 0;
                        let priceWithDiscount = discount > 0 ? item.price * (1 - discount / 100) : item.price;

                        // Определяем sizeId и size из всех возможных источников
                        let sizeId = item.sizeId || item.variant?.sizeId || item.variant?.id || null;
                        let size = item.selectedSize || item.variant?.size || item.variant?.value || item.size || null;
                        
                        // Если sizeId не найден, ищем по selectedSize в массиве размеров
                        if (!sizeId && item.selectedSize && item.sizes && Array.isArray(item.sizes)) {
                            const foundSize = item.sizes.find(sz => sz.value === item.selectedSize || sz.size === item.selectedSize);
                            if (foundSize) sizeId = foundSize.id;
                        }
                        
                        // Если size не найден, ищем по sizeId
                        if (!size && sizeId && item.sizes && Array.isArray(item.sizes)) {
                            const foundSize = item.sizes.find(sz => sz.id === sizeId);
                            if (foundSize) size = foundSize.value || foundSize.size;
                        }
                        
                        // Формируем полный variant для фронта
                        const variant = {
                            size: size,
                            sizeId: sizeId,
                            color: item.variant?.color || item.color || undefined,
                            name: item.variant?.name || undefined, // Для игрушек
                            id: item.variant?.id || undefined, // ID варианта для игрушек
                            storeName: item.variant?.storeName || undefined,
                            storeId: item.variant?.storeId || item.store_id || undefined
                        };

                        return {
                            product_id: item.id,
                            quantity: item.quantity,
                            price: item.price,
                            discount: item.discount || 0,
                            price_with_discount: priceWithDiscount,
                            final_price: priceWithDiscount,
                            sizeId: sizeId,
                            size: size,
                            store_id: item.store_id || null,
                            variant: variant
                        };
                    }),
                    discount_percent: Math.round(userDiscount),
                    discount_amount: storePersonalDiscount,
                    total: storeTotal,
                    total_with_discount: totalWithAllDiscounts,
                    store_id: storeId === 'no_store' ? null : storeId,
                    paymentType: 'cash',
                };

                console.log('📦 [BASKET] orderData для отправки:', orderData);
                try {
                    await createOrder(orderData);
                } catch (e) {
                    allSuccess = false;
                    errorMsg = e?.response?.data?.message || 'Ошибка при оформлении заказа';
                    break;
                }
            }
            if (allSuccess) {
                setOrderPlaced(true);
                user.clearBasket();
                if (user.basket.length > 0) {
                    const ids = user.basket.map(item => item.id);
                    const updatedProducts = await fetchProductsByIds(ids);
                    updatedProducts.forEach(updated => {
                        product.updateProductInStore(updated);
                    });
                }
                setTimeout(() => {
                    setShowOrderModal(false);
                    setOrderPlaced(false);
                    navigate('/orders');
                }, 2000);
            } else {
                setOrderError(errorMsg);
            }
        } catch (e) {
            setOrderError(e?.response?.data?.message || 'Ошибка при оформлении заказа');
        }
    };

    const rawBasket = toJS(user.basket);
    console.log('DEBUG RAW BASKET', rawBasket);

    // ГАРАНТИРОВАННЫЙ ПАТЧ: функция для поиска названия магазина по store_id
    const getStoreNameById = (storeId) => {
        const allStores = (user && user.stores) || (product && product.stores) || [];
        if (!storeId || !Array.isArray(allStores)) return '-';
        const store = allStores.find(s => String(s.id) === String(storeId));
        return store ? store.name : '-';
    };

    return (
        <Container className="basket-page mt-4">
            <h1 className="basket-title">Корзина</h1>
            
            {user.basket.length === 0 ? (
                <div className="empty-basket">
                <p>Ваша корзина пуста</p>
                    <Button 
                        variant="primary" 
                        onClick={() => navigate('/shop')}
                    >
                        Перейти в каталог
                    </Button>
                </div>
            ) : (
                <>
                    <Row>
                        <Col md={8}>
                            {rawBasket.map((item, index) => {
                                const discount = Number(item.discount) || 0;
                                const priceWithProductDiscount = discount > 0 ? item.price * (1 - discount / 100) : item.price;
                                // ГАРАНТИРОВАННЫЙ ПАТЧ: всегда ищем название магазина
                                let storeName = item.store_name || (item.store_id && getStoreNameById(item.store_id)) || '-';
                                console.log('DEBUG VARIANT', item.variant);
                                return (
                                    <Card key={index} className="basket-item mb-3">
                                        <Card.Body>
                                            <Row>
                                                <Col md={3} className="basket-item-image-col d-flex align-items-center justify-content-center">
                                                    <div style={{width: 120, height: 120, background: '#f8f9fa', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', border: '1px solid #eee'}}>
                                                        <img
                                                            src={getImageUrl(item.img)}
                                                            alt={item.name}
                                                            loading="lazy"
                                                            decoding="async"
                                                            width={120}
                                                            height={120}
                                                            style={{maxWidth: '100%', maxHeight: '100%', objectFit: 'contain'}}
                                                        />
                                                    </div>
                                                </Col>
                                                <Col md={9}>
                                                    <div className="basket-item-info">
                                                        <h3 className="basket-item-title">{item.name}</h3>
                                                        {item.variant && typeof item.variant === 'object' && (
                                                            <>
                                                                {/* Для обуви и одежды показываем размер и цвет */}
                                                                {(item.variant.size || item.selectedSize || (typeof item.size === 'string' ? item.size : null)) && (
                                                                    <p className="basket-item-size">Размер: {item.variant.size || item.selectedSize || (typeof item.size === 'string' ? item.size : '')}</p>
                                                                )}
                                                                {item.variant.color && <p className="basket-item-color">Цвет: {item.variant.color}</p>}
                                                                {/* Для игрушек показываем название варианта */}
                                                                {item.variant.name && !item.variant.size && (
                                                                    <p className="basket-item-variant">Вариант: {item.variant.name}</p>
                                                                )}
                                                            </>
                                                        )}
                                                        {/* Если variant это объект игрушки, но не отрендерился выше */}
                                                        {!item.variant && item.size && typeof item.size === 'object' && item.size.name && (
                                                            <p className="basket-item-variant">Вариант: {item.size.name}</p>
                                                        )}
                                                        {item.store_id && (
                                                            <p className="basket-item-store">
                                                                Магазин: {storeName}
                                                            </p>
                                                        )}
                                                        <div className="basket-item-price" style={{display: 'flex', alignItems: 'center', gap: 12}}>
                                                            {discount > 0 ? (
                                                                <>
                                                                    <span className="original-price">
                                                                        {item.price.toLocaleString(undefined, {maximumFractionDigits: 2})} ₽
                                                                    </span>
                                                                    <span className="discounted-price">
                                                                        {priceWithProductDiscount.toLocaleString(undefined, {maximumFractionDigits: 2})} ₽
                                                                    </span>
                                                                    <span style={{color: '#888', fontSize: 16}}>
                                                                        × {item.quantity} = {(priceWithProductDiscount * item.quantity).toLocaleString(undefined, {maximumFractionDigits: 2})} ₽
                                                                    </span>
                                                                </>
                                                            ) : (
                                                                <span style={{ color: '#222', fontWeight: 400, fontSize: 22 }}>
                                                                    {item.price.toLocaleString(undefined, {maximumFractionDigits: 2})} ₽ × {item.quantity} = {(item.price * item.quantity).toLocaleString(undefined, {maximumFractionDigits: 2})} ₽
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="basket-item-actions">
                                                            <Form.Control
                                                                type="number"
                                                                min="1"
                                                                value={item.quantity}
                                                                onChange={(e) => {
                                                                    const newQuantity = parseInt(e.target.value);
                                                                    if (newQuantity > 0) {
                                                                        user.updateBasketItem(index, newQuantity);
                                                                    }
                                                                }}
                                                                className="quantity-input"
                                                            />
                                                            <Button 
                                                                variant="outline-danger"
                                                                onClick={() => user.removeFromBasket(index)}
                                                            >
                                                                Удалить
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>
                                );
                            })}
                        </Col>
                        <Col md={4}>
                            <Card className="basket-summary">
                                <Card.Body>
                                    <h3>Итого</h3>
                                    <div className="basket-total">
                                        <div>Сумма товаров со скидкой: {totalProductDiscount.toLocaleString(undefined, {maximumFractionDigits: 2})} ₽</div>
                                        {userDiscount > 0 && (
                                            <div>Персональная скидка: -{personalDiscountAmount.toLocaleString(undefined, {maximumFractionDigits: 2})} ₽ ({userDiscount}%)</div>
                                        )}
                                        <div style={{ fontWeight: 'bold', fontSize: 20, marginTop: 8 }}>
                                            Итого: {totalWithAllDiscounts.toLocaleString(undefined, {maximumFractionDigits: 2})} ₽
                                        </div>
                                    </div>
                                    <Button
                                        variant="primary"
                                        className="w-100 mt-3"
                                        onClick={() => setShowOrderModal(true)}
                                        disabled={user.basket.length === 0}
                                    >
                                        Оформить заказ
                                    </Button>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

            <Modal 
                show={showOrderModal} 
                onHide={handleModalClose}
                onExited={handleModalClose}
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Оформление заказа</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {orderPlaced ? (
                        <Alert variant="success" className="text-center fs-5 fw-bold">
                            Спасибо за заказ! Сейчас вы будете перенаправлены в раздел заказов.
                        </Alert>
                    ) : (
                        <>
                            <ul className="mb-3">
                                {rawBasket.map((item, idx) => (
                                    <li key={idx}>
                                        {item.name} — <b>{item.store_name || '-'}</b>
                                    </li>
                                ))}
                            </ul>
                            {(() => {
                                const storeIds = user.basket.map(item => item.store_id).filter(Boolean);
                                const uniqueStoreIds = [...new Set(storeIds)];
                                return uniqueStoreIds.length > 1 ? (
                                    <Alert variant="danger" className="text-center fs-5 fw-bold">
                                        ВНИМАНИЕ! В вашем заказе товары из разных магазинов.<br />
                                        Вы уверены, что хотите оформить такой заказ?<br />
                                        Товары будут доступны в разных магазинах!
                                    </Alert>
                                ) : null;
                            })()}
                            <div className="d-flex justify-content-end mt-4">
                                <Button variant="primary" onClick={handleOrder}>
                                    Оформить заказ
                                </Button>
                            </div>
                            {orderError && (
                                <Alert variant="danger" className="mt-3 text-center">{orderError}</Alert>
                            )}
                        </>
                    )}
                </Modal.Body>
            </Modal>
                        </>
                    )}
        </Container>
    );
});

export default Basket;